import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, SaveDraftClickBiEvent } from '@wix/da-bi/pkg/events';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import HookFormDevtools from '@wix/da-shared-react/pkg/utils/hookform/Devtools';
import DesktopOriginalsDeliveryForm from '../../layouts/desktop/OriginalsDeliveryForm';
import MobileOriginalsDeliveryForm from '../../layouts/mobile/OriginalsDeliveryForm';
import SubmitButton from '../../layouts/common/SubmitButton';
import { useSubmitForm } from '../../hooks/useSubmitForm';
import { useSubmitFormStatus } from '../../hooks/useSubmitFormStatus';
import { submitOriginalDelivery } from '../../redux/actions/originals';
import { SubmitFormProvider } from '../../context';
import { SubmitFormParams } from '../../types';

import s from './OriginalsDeliveryFormPopup.scss';

export interface Props {
  params: SubmitFormParams;
  requestClose: () => void;
}

const OriginalsDeliveryFormPopup: React.FC<Props> = ({
  params,
  requestClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const form = useSubmitForm({
    params,
    requestClose,
  });

  const { deviationId, originalsDeviationId, originalsResponseId } = params;
  const {
    formContext,
    initResponse,
    requestDeinitializeAndClose,
    requestSafeClose,
    scrollableRef,
    submitAdapter,
  } = form;
  const deviation = initResponse?.deviation;

  const { isBusy } = useSubmitFormStatus();
  const { watch, control, handleSubmit } = formContext;
  const { isInitialized } = watch(['isInitialized']);

  const requestSubmit = useCallback(
    (options: { doDeliver: boolean; onSuccess?: () => void }) => {
      handleSubmit(data => {
        const formData = submitAdapter(data, formContext.formState);
        dispatch(
          submitOriginalDelivery({
            formData: {
              ...formData,
              originalsDeviationId,
              originalsResponseId,
            },
            ...options,
          })
        );
      })();
    },
    [
      handleSubmit,
      dispatch,
      originalsDeviationId,
      originalsResponseId,
      submitAdapter,
      formContext.formState,
    ]
  );

  const onSaveSuccess = useCallback(() => {
    requestDeinitializeAndClose();
  }, [requestDeinitializeAndClose]);

  return (
    <SubmitFormProvider {...form}>
      <Modal
        isOpen
        isFullscreen
        heading={t('deviationSubmit.title.originalsDelivery')}
        preventScroll
        footerRight={
          isInitialized
            ? [
                <TextButton
                  key="footer-save"
                  size="large"
                  variant="main"
                  disabled={isBusy}
                  biData={BiData<SaveDraftClickBiEvent>({
                    evid: 17,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviationId,
                  })}
                  onClick={() =>
                    requestSubmit({
                      doDeliver: false,
                      onSuccess: onSaveSuccess,
                    })
                  }
                >
                  {t('deviationSubmit.form.button.saveDraft.originalsDelivery')}
                </TextButton>,
                <SubmitButton
                  key="footer-submit"
                  requestSubmit={() =>
                    requestSubmit({
                      doDeliver: true,
                    })
                  }
                />,
              ]
            : undefined
        }
        showHeaderBorder
        showFooterBorder
        layoutClassName={s['layout']}
        contentClassName={s['content']}
        footerPadding="horizontally-less"
        onRequestClose={requestSafeClose}
        withScrollableContentWrapper={isMobile}
        bodyScrollLockOptions={{
          allowTouchMove: (el: HTMLElement) => {
            return el.closest('.editor-scrollable') !== null;
          },
        }}
        innerScrollRef={
          isMobile ? el => (scrollableRef.current = el) : undefined
        }
      >
        {!isInitialized && <LoadingIndicator />}
        {isInitialized && !isMobile && <DesktopOriginalsDeliveryForm />}
        {isInitialized && isMobile && <MobileOriginalsDeliveryForm />}
        <HookFormDevtools control={control} />
      </Modal>
    </SubmitFormProvider>
  );
};
OriginalsDeliveryFormPopup.displayName = 'OriginalsDeliveryFormPopup';

export default OriginalsDeliveryFormPopup;
